import { RestUrl } from "@/commons/constants/RestUrl";
import UrlFormatter from "@/commons/utils/UrlFormatter";
import CommonService from "@/commons/services/CommonService";
import LoginUser from "@/store/LoginUser";
import { HitomeqErrorType } from "@/commons/models/SystemError";

/**
 * ### カレンダーイベント API 操作クラス
 */
export default class CalendarEvents {
  /** 共通サービス */
  private commonService: CommonService;

  /**
   * ### コンストラクター
   *
   * - 共通サービスが渡されればそれを使用する
   * - 共通サービスが渡されなければ新しいインスタンスを作る
   *
   * @param commonService 共通サービス
   */
  constructor(commonService?: CommonService) {
    this.commonService = commonService ? commonService : new CommonService();
    return;
  }

  /**
   * ### GET CalendarEvent
   */
  public async get(userStore: LoginUser, calendarEventId: number): Promise<CalendarEventGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      calendarEventId: calendarEventId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.CalendarEvent, formatParams);

    const response = await this.commonService.executeGet<CalendarEventGetModel>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET CalendarEvents
   */
  public async gets(userStore: LoginUser, queryParams?: { startIndex?: number; endIndex?: number }): Promise<Array<CalendarEventGetModel>> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.CalendarEvents, formatParams);

    const params: any = queryParams ? queryParams : {};
    const response = await this.commonService.executeGet<Array<CalendarEventGetModel>>(userStore, requestUrl, this.constructor.name, params, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * ### GET CalendarEventsCount
   */
  public async getCount(userStore: LoginUser): Promise<number> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.CalendarEvents, formatParams);
    const response = await this.commonService.executeGet<{ count: number }>(userStore, `${requestUrl}/count`, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data.count;
  }

  /**
   * ### POST CalendarEvent
   */
  public async post(userStore: LoginUser, requestBody: CalendarEventPostModel): Promise<CalendarEventGetModel> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.CalendarEvents, formatParams);

    const response = await this.commonService.executePost<CalendarEventGetModel>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * PATCH CalendarEvent
   */
  public async patch(userStore: LoginUser, calendarEventId: number, requestBody: CalendarEventPatchModel): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      calendarEventId: calendarEventId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.CalendarEvent, formatParams);

    const response = await this.commonService.executePatch<any>(userStore, requestUrl, requestBody, this.constructor.name, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }

  /**
   * DELETE CalendarEvent
   */
  public async delete(userStore: LoginUser, calendarEventId: number): Promise<any> {
    const formatParams: any = {
      facilityId: userStore.loginUser.facilityId,
      calendarEventId: calendarEventId
    };
    const requestUrl: string = UrlFormatter.format(RestUrl.CalendarEvent, formatParams);

    const response = await this.commonService.executeDelete<any>(userStore, requestUrl, this.constructor.name, {}, HitomeqErrorType.REQUEST_RETRY, 0);

    return response.data;
  }
}

export interface CalendarEventGetModel {
  /** 施設ID */
  facilityId: number;
  /** カレンダーイベントを示す ID */
  id: number;
  /** 開始日時 */
  startDatetime: number;
  /** 終了日時 */
  endDatetime: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string | null;
  /** 色情報 */
  color: CalendarColor;
  /** 予定のフィルタリング用種別(0：施設全体、1：利用者グループ) */
  filterType: CalendarFilterType;
  /** 外出手段の選択の使用有無 */
  residentGroups: Array<number>;
  /** 記載者名 */
  writtenBy: string;
  /** 記載者ID */
  writerId: string;

  /** 論理削除フラグ */
  deleteFlag: boolean;
  /** 最終更新日 */
  updated: number;
  /** 作成した日付(YYYYMMDDhhmmssSSS) */
  created: number;
}

export interface CalendarColor {
  /** 背景色 */
  background: string;
  /** 枠線 */
  border: string;
  /** 文字色 */
  font: string;
}

export enum CalendarFilterType {
  /** 施設全体 */
  FACILITY = 0,
  /** 利用者グループ */
  RESIDENT_GROUP = 1
}

export interface CalendarEventPostModel {
  /** 開始日時 */
  startDatetime: number;
  /** 終了日時 */
  endDatetime: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string | null;
  /** 色情報 */
  color: CalendarColor;
  /** 予定のフィルタリング用種別(0：施設全体、1：利用者グループ) */
  filterType: CalendarFilterType;
  /** 外出手段の選択の使用有無 */
  residentGroups: Array<number>;
}

export interface CalendarEventPatchModel {
  /** 開始日時 */
  startDatetime: number;
  /** 終了日時 */
  endDatetime: number;
  /** タイトル */
  title: string;
  /** 本文 */
  text: string | null;
  /** 色情報 */
  color: CalendarColor;
  /** 予定のフィルタリング用種別(0：施設全体、1：利用者グループ) */
  filterType: CalendarFilterType;
  /** 外出手段の選択の使用有無 */
  residentGroups: Array<number>;
  /** 更新日時 */
  updated: number;
}
